import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Megaphone, 
  LayoutTemplate, 
  Layers,
  Users,
  Settings, 
  CreditCard,
  Sparkles,
  LogOut,
  Shield,
  User,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  BarChart3,
  Building2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

// Navigation items with optional role restriction
const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Campañas', href: '/campaigns', icon: Megaphone },
  { name: 'Plantillas', href: '/templates', icon: LayoutTemplate },
  { name: 'Contactos', href: '/contacts', icon: Users },
  { name: 'Emails Enviados', href: '/emails', icon: Mail },
  { name: 'Estadísticas', href: '/stats', icon: BarChart3 },
  { name: 'Mi Empresa', href: '/my-company', icon: Building2 },
  { name: 'Sectores', href: '/sectors', icon: Layers, roles: ['admin', 'manager'] },
  { name: 'Configuración', href: '/settings', icon: Settings },
];

const roleLabels = {
  admin: { label: 'Administrador', color: 'bg-red-500' },
  manager: { label: 'Gerente', color: 'bg-amber-500' },
  employee: { label: 'Empleado', color: 'bg-blue-500' }
};

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, logout, isAdmin, isManager } = useAuth();
  
  // Collapsed state - persisted in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', isCollapsed.toString());
    // Dispatch custom event so other components can react
    window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: { collapsed: isCollapsed } }));
  }, [isCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item => {
    if (!item.roles) return true;
    if (isAdmin) return true;
    if (isManager && item.roles.includes('manager')) return true;
    return item.roles.includes(user?.role);
  });

  const roleInfo = roleLabels[user?.role] || roleLabels.employee;

  const NavItem = ({ item, isActive }) => {
    const content = (
      <NavLink
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase()}`}
        className={`sidebar-link flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all
          ${isActive 
            ? 'active text-white' 
            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
          }
          ${isCollapsed ? 'justify-center px-3' : ''}
        `}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && <span>{item.name}</span>}
      </NavLink>
    );

    if (isCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
            {item.name}
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <TooltipProvider>
      <aside 
        className={`bg-slate-900 text-slate-300 flex flex-col h-screen fixed left-0 top-0 z-50 transition-all duration-300 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`} 
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`p-4 border-b border-slate-700 ${isCollapsed ? 'px-3' : 'p-6'}`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="font-bold text-lg text-white font-[Manrope]">MarketFlow</h1>
                <span className="text-xs text-slate-400">v2.0</span>
              </div>
            )}
          </div>
        </div>

        {/* Toggle button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-700 hover:bg-slate-600 rounded-full flex items-center justify-center text-slate-300 hover:text-white transition-colors shadow-lg border border-slate-600"
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>

        {/* Navigation */}
        <nav className={`flex-1 py-6 space-y-1 overflow-y-auto ${isCollapsed ? 'px-2' : 'px-3'}`}>
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/' && location.pathname.startsWith(item.href));
            
            return (
              <NavItem key={item.name} item={item} isActive={isActive} />
            );
          })}
        </nav>

        {/* Credits indicator */}
        <div className={`border-t border-slate-700 ${isCollapsed ? 'p-2' : 'p-4'}`}>
          {isCollapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <NavLink 
                  to="/settings?tab=credits" 
                  className="flex items-center justify-center p-3 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
                  data-testid="credits-indicator"
                >
                  <CreditCard className="w-5 h-5 text-indigo-400" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                Créditos IA
              </TooltipContent>
            </Tooltip>
          ) : (
            <NavLink 
              to="/settings?tab=credits" 
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
              data-testid="credits-indicator"
            >
              <CreditCard className="w-5 h-5 text-indigo-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-400">Créditos IA</p>
                <p className="text-sm font-semibold text-white" data-testid="credits-balance">--</p>
              </div>
            </NavLink>
          )}
        </div>

        {/* User section */}
        <div className={`border-t border-slate-700 ${isCollapsed ? 'p-2' : 'p-4'}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {isCollapsed ? (
                <button 
                  className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-slate-800 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                </button>
              ) : (
                <button 
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors text-left"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate" data-testid="user-name">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {company?.name || 'Mi Empresa'}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isCollapsed ? "center" : "end"} side={isCollapsed ? "right" : "top"} className="w-56">
              <DropdownMenuLabel>
                <div className="flex items-center gap-2">
                  <span>{user?.email}</span>
                </div>
                <div className="mt-1">
                  <Badge variant="secondary" className={`${roleInfo.color} text-white text-xs`}>
                    <Shield className="w-3 h-3 mr-1" />
                    {roleInfo.label}
                  </Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="user-settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Configuración
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout} 
                className="text-red-500 focus:text-red-500"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
